import { localGet, localRemove, localSet } from '@/utils/index'

const WALLET_STORAGE_KEY = 'zh'

export function getEthereumProvider() {
  if (typeof window === 'undefined') {
    return null
  }

  return window.ethereum || null
}

export function normalizeAccount(account) {
  return typeof account === 'string' && account ? account : null
}

export function getStoredWalletAccount() {
  return normalizeAccount(localGet(WALLET_STORAGE_KEY))
}

export function setStoredWalletAccount(account) {
  const normalizedAccount = normalizeAccount(account)

  if (normalizedAccount) {
    localSet(WALLET_STORAGE_KEY, normalizedAccount)
  }
}

export function clearStoredWalletAccount() {
  localRemove(WALLET_STORAGE_KEY)
}

export async function getConnectedAccounts() {
  const ethereum = getEthereumProvider()

  if (!ethereum) {
    return []
  }

  const accounts = await ethereum.request({ method: 'eth_accounts' })
  return Array.isArray(accounts) ? accounts : []
}

export async function getActiveWalletAccount() {
  const accounts = await getConnectedAccounts()

  if (accounts.length === 0) {
    clearStoredWalletAccount()
    return null
  }

  const storedAccount = getStoredWalletAccount()
  const matchedAccount = accounts.find(account => {
    return (
      storedAccount &&
      account &&
      account.toLowerCase() === storedAccount.toLowerCase()
    )
  })

  const activeAccount = matchedAccount || accounts[0]
  setStoredWalletAccount(activeAccount)
  return activeAccount
}

export async function requestWalletConnection() {
  const ethereum = getEthereumProvider()

  if (!ethereum) {
    throw new Error('Please install MetaMask wallet')
  }

  const accounts = await ethereum.request({ method: 'eth_requestAccounts' })
  const account = Array.isArray(accounts) ? accounts[0] : null

  if (!account) {
    throw new Error('Not able to get accounts')
  }

  setStoredWalletAccount(account)
  return account
}

export async function requestWalletPermissions() {
  const ethereum = getEthereumProvider()

  if (!ethereum) {
    throw new Error('Please install MetaMask wallet')
  }

  await ethereum.request({
    method: 'wallet_requestPermissions',
    params: [{ eth_accounts: {}}]
  })

  return getActiveWalletAccount()
}

export async function getCurrentChainId() {
  const ethereum = getEthereumProvider()

  if (!ethereum) {
    return null
  }

  return ethereum.request({ method: 'eth_chainId' })
}

export function isTargetChain(chainId) {
  if (!chainId || !process.env.VUE_APP_CHAINID) {
    return false
  }

  return chainId.toLowerCase() === process.env.VUE_APP_CHAINID.toLowerCase()
}

function isUnrecognizedChainError(error) {
  return error && Number(error.code) === 4902
}

export async function ensureTargetChain() {
  const ethereum = getEthereumProvider()

  if (!ethereum) {
    throw new Error('Please install MetaMask wallet')
  }

  const currentChainId = await getCurrentChainId()
  if (isTargetChain(currentChainId)) {
    return currentChainId
  }

  try {
    await ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: process.env.VUE_APP_CHAINID }]
    })
  } catch (error) {
    if (!isUnrecognizedChainError(error)) {
      throw error
    }

    await ethereum.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: process.env.VUE_APP_CHAINID,
          chainName: process.env.VUE_APP_CHAIN_NAME,
          rpcUrls: JSON.parse(process.env.VUE_APP_RPC_URLS),
          nativeCurrency: {
            name: 'UNIT',
            symbol: 'UNIT',
            decimals: 18
          }
        }
      ]
    })
  }

  return getCurrentChainId()
}

export function subscribeWalletEvents(handlers = {}) {
  const ethereum = getEthereumProvider()

  if (!ethereum || typeof ethereum.on !== 'function') {
    return () => {}
  }

  const accountHandler = accounts => {
    if (typeof handlers.onAccountsChanged === 'function') {
      handlers.onAccountsChanged(Array.isArray(accounts) ? accounts : [])
    }
  }

  const chainHandler = chainId => {
    if (typeof handlers.onChainChanged === 'function') {
      handlers.onChainChanged(chainId)
    }
  }

  ethereum.on('accountsChanged', accountHandler)
  ethereum.on('chainChanged', chainHandler)

  return () => {
    if (typeof ethereum.removeListener === 'function') {
      ethereum.removeListener('accountsChanged', accountHandler)
      ethereum.removeListener('chainChanged', chainHandler)
    }
  }
}
